import FakeStoreApi from "./FakeStoreApi";
import RealStoreApi from "./RealStoreApi";
import CommonRequestBodyDto from "../dto/common/CommonRequestBodyDto";
import StoreSaveReqDto from "../dto/store/StoreSaveReqDto";
import StoreUpdateReqDto from "../dto/store/StoreUpdateReqDto";
import { StoreRevenuePeriodType } from "../dto/store/StoreTotalRevenueDto";
import SuccessResponse from "../dto/common/SuccessResponse";
import StoreDeviceDto from "../dto/store/StoreDeviceDto";

class StoreApi {
    fakeApi = false;
    fake = new FakeStoreApi();
    real = new RealStoreApi();

    getStoreDetail = ({ storeId }: { storeId: string }) => {
        if (this.fakeApi) {
            return this.fake.getStoreDetail();
        } else {
            return this.real.getStoreDetail({ storeId });
        }
    };

    getStoreList = ({
        page,
        pageSize,
        shopCode,
        shopName,
        phoneNumber,
        address,
        states,
    }: {
        page: number;
        pageSize: number;
        shopCode?: string;
        shopName?: string;
        phoneNumber?: string;
        address?: string;
        states?: string;
    }) => {
        if (this.fakeApi) {
            return this.fake.getStoreList({ shopName, shopCode });
        } else {
            return this.real.getStoreList({ page, pageSize, shopCode, shopName, phoneNumber, address, states });
        }
    };

    getStoreAdminList = ({
        page,
        pageSize,
        franchisesCode,
    }: {
        page: number;
        pageSize: number;
        franchisesCode: string;
    }) => {
        if (this.fakeApi) {
            return this.fake.getStoreAdminList();
        } else {
            return this.real.getStoreAdminList({ page, pageSize, franchisesCode });
        }
    };

    saveStore = (reqDto: CommonRequestBodyDto<StoreSaveReqDto>) => {
        if (this.fakeApi) {
            return this.fake.saveStore();
        } else {
            return this.real.saveStore(reqDto);
        }
    };

    updateStore = (reqDto: CommonRequestBodyDto<StoreUpdateReqDto>) => {
        if (this.fakeApi) {
            return this.fake.updateStore();
        } else {
            return this.real.updateStore(reqDto);
        }
    };

    deleteStore = ({ storeId }: { storeId: string }) => {
        if (this.fakeApi) {
            return this.fake.deleteStore();
        } else {
            return this.real.deleteStore({ storeId });
        }
    };

    getStoreReportDetail = ({
        storeId,
        period,
        startDate,
        endDate,
    }: {
        storeId: string;
        period: "Day" | "Week" | "Month" | "Year";
        startDate: string;
        endDate: string;
    }) => {
        if (this.fakeApi) {
            return this.fake.getStoreReportDetail();
        } else {
            return this.real.getStoreReportDetail({ storeId, period, startDate, endDate });
        }
    };

    getStoreDeviceStatus = ({ storeId }: { storeId: string }) => {
        if (this.fakeApi) {
            return this.fake.getStoreDeviceStatus();
        } else {
            return this.real.getStoreDeviceStatus({ storeId });
        }
    };

    getStoreDeviceStatusSummary = ({ storeId }: { storeId: string }) => {
        if (this.fakeApi) {
            return this.fake.getStoreDeviceStatusSummary();
        } else {
            return this.real.getStoreDeviceStatusSummary({ storeId });
        }
    };

    getStoreRecentRevenue = ({
        storeId,
        startDate,
        endDate,
    }: {
        storeId: string;
        startDate: string;
        endDate: string;
    }) => {
        if (this.fakeApi) {
            return this.fake.getStoreRecentRevenue();
        } else {
            return this.real.getStoreRecentRevenue({ storeId, startDate, endDate });
        }
    };

    getStoreRevenueSummary = ({ today }: { today: string }) => {
        if (this.fakeApi) {
            return this.fake.getStoreRevenueSummary();
        } else {
            return this.real.getStoreRevenueSummary({ today });
        }
    };

    getStoreRevenueSummaryByStore = ({ storeId, today }: { storeId: string; today: string }) => {
        if (this.fakeApi) {
            return this.fake.getStoreRevenueSummaryByStore();
        } else {
            return this.real.getStoreRevenueSummaryByStore({ storeId, today });
        }
    };

    getStoreRevenueByStore = ({
        storeId,
        periodType,
        startDate,
        endDate,
    }: {
        storeId: string;
        periodType: StoreRevenuePeriodType;
        startDate: string;
        endDate: string;
    }) => {
        if (this.fakeApi) {
            return this.fake.getStoreRevenueByStore({ storeId, periodType, startDate, endDate });
        } else {
            return this.real.getStoreRevenueByStore({ storeId, periodType, startDate, endDate });
        }
    };

    getStoreQrCode = ({ storeId }: { storeId: string }) => {
        if (this.fakeApi) {
            return this.fake.getStoreQrCode();
        } else {
            return this.real.getStoreQrCode({ storeId });
        }
    };

    async saveStoreImage({ storeId, image }: { storeId: string; image: File }): Promise<SuccessResponse<object>> {
        return this.real.saveStoreImage({ storeId, image });
    }

    async getStoreImages({ storeId }: { storeId: string }): Promise<SuccessResponse<object>> {
        return this.real.getStoreImages({ storeId });
    }

    getGpsStoreList({
        radius,
        userLatitude,
        userLongitude,
        pageSize,
    }: {
        radius: string;
        userLatitude: string;
        userLongitude: string;
        pageSize: number;
    }): Promise<SuccessResponse<object>> {
        return this.real.getGpsStoreList({ radius, userLatitude, userLongitude, pageSize });
    }

    async syncShopCode(): Promise<SuccessResponse<object>> {
        return this.real.syncShopCode();
    }

    async getStoreDevice({ storeId }: { storeId: string }): Promise<SuccessResponse<StoreDeviceDto[]>> {
        if (this.fakeApi) {
            return this.fake.getStoreDevice({ storeId });
        } else {
            return this.real.getStoreDevice({ storeId });
        }
    }

    getDeviceMemo = ({ deviceId }: { deviceId: string }) => {
        if (this.fakeApi) {
            return this.fake.getDeviceMemo();
        } else {
            return this.real.getDeviceMemo({ deviceId });
        }
    };

    createDeviceMemo = ({ deviceId, memo }: { deviceId: string; memo: string }) => {
        if (this.fakeApi) {
            return this.fake.createDeviceMemo();
        } else {
            return this.real.createDeviceMemo({ deviceId, memo });
        }
    };

    updateDeviceMemo = ({ deviceId, memoId, memo }: { deviceId: string; memoId: number; memo: string }) => {
        if (this.fakeApi) {
            return this.fake.updateDeviceMemo();
        } else {
            return this.real.updateDeviceMemo({ deviceId, memoId, memo });
        }
    };

    deleteDeviceMemo = ({ deviceId, memoId }: { deviceId: string; memoId: number }) => {
        if (this.fakeApi) {
            return this.fake.deleteDeviceMemo();
        } else {
            return this.real.deleteDeviceMemo({ deviceId, memoId });
        }
    };
}

export default StoreApi;
